import { Calculator } from 'lucide-react';
import { formatMinor, TaxLineRow, TaxReportCard, TaxStatCell, TaxStatStrip } from './taxReportShared';

interface BreakdownLine {
  label: string;
  amountMinor: number;
}

interface PaymentOnAccount {
  dueDate: string;
  amountMinor: number;
}

interface Data {
  type: string;
  periodEnd: string;
  netProfitMinor: number;
  ukEstimate: {
    estimatedTaxMinor: number;
    breakdown: BreakdownLine[];
    paymentsOnAccount: PaymentOnAccount[];
  };
  totalEstimatedTaxMinor: number;
}

export function TaxEstimateUkReport({ data }: { data: Record<string, unknown> }) {
  const d = data as unknown as Data;
  const breakdown = d.ukEstimate?.breakdown ?? [];
  const payments = d.ukEstimate?.paymentsOnAccount ?? [];

  return (
    <TaxReportCard icon={Calculator} title="Tax Estimate (UK)" badge={`As of ${d.periodEnd}`}>
      <TaxStatStrip>
        <TaxStatCell label="Net profit YTD" value={formatMinor(d.netProfitMinor, 'GBP')} />
        <TaxStatCell
          label="Estimated tax"
          value={formatMinor(d.totalEstimatedTaxMinor, 'GBP')}
          emphasis
        />
      </TaxStatStrip>

      <table className="w-full text-sm">
        <thead>
          <tr className="bg-surface-50/60 text-[11px] uppercase tracking-[0.06em] text-surface-500">
            <th className="px-4.5 py-2 text-left font-medium">Income tax &amp; NI</th>
            <th className="px-4.5 py-2 text-right font-medium">Amount</th>
          </tr>
        </thead>
        <tbody>
          {breakdown.map((line) => (
            <TaxLineRow
              key={line.label}
              label={line.label}
              value={formatMinor(line.amountMinor, 'GBP')}
            />
          ))}
          <TaxLineRow
            label="Total estimated tax"
            value={formatMinor(d.totalEstimatedTaxMinor, 'GBP')}
            strong
          />
        </tbody>
      </table>

      {payments.length > 0 ? (
        <div className="border-t border-surface-100 px-4.5 py-3">
          <div className="mb-1.5 text-[11px] font-medium uppercase tracking-[0.06em] text-surface-500">
            Payments on account
          </div>
          <div className="space-y-1">
            {payments.map((p) => (
              <div key={p.dueDate} className="flex items-center justify-between text-[13px]">
                <span className="text-surface-600">Due {p.dueDate}</span>
                <span className="font-mono tabular-nums text-surface-900">
                  {formatMinor(p.amountMinor, 'GBP')}
                </span>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </TaxReportCard>
  );
}
